import _ from 'lodash';
import React from 'react';
import * as config from 'config';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

/**
 * Rules of Feature Flags
 *
 * Flags are booleans.
 * Flags are defined as positive assertions, e.g. enableThis, allowThat.
 * Flags MUST be defined with a default in config.js.
 * Flags MAY be defined in the environment, prefixed with REACT_APP_FFLAG_, e.g. REACT_APP_FFLAG_ENABLE_THIS.
 * Flags MAY be defined in a querystring parameter, e.g. enableThis=true.
 * Flags defined in the environment or querystring parameter will be matched against and override default defined in config.
 * Flag keys defined in the environment or querystring are coerced to camelCase.
 * Flag values defined in the environment or querystring are coerced to Boolean, if possible.
 * Querystring > environment > default flags.
 *
 */

const TRUTHY_FLAGS = ['true', 'yes', '1'];
const FALSEY_FLAGS = ['false', 'no', '0'];
const coerceBoolean = (originalValue) => {
  const lowerValue = _.toLower(originalValue);
  const isLowerValFalsy = _.includes(FALSEY_FLAGS, lowerValue) ? false : originalValue;
  return _.includes(TRUTHY_FLAGS, lowerValue) ? true : isLowerValFalsy;
};

export const flagsFromObject = (object) => {
  const defaultFeatureFlags = config.getDefaultFeatureFlags();
  return _(object)
    .transform((accumulator, value, key) => {
      accumulator[_.camelCase(key)] = coerceBoolean(value);
    })
    .pickBy((__, key) => _.has(defaultFeatureFlags, key))
    .value();
};

export const flagsFromEnvironment = (
  environment = process.env,
  KEY_PREFIX = config.FEATURE_FLAG_ENV_PREFIX,
) => _(environment)
  .pickBy((__, key) => KEY_PREFIX && key.startsWith(KEY_PREFIX))
  .transform((accumulator, value, key) => {
    const [, flagName] = _.split(key, KEY_PREFIX);
    accumulator[flagName] = value;
  })
  .thru(flagsFromObject)
  .value();

export const flagsFromQueryString = (search) => {
  const parsedQueryString = qs.parse(search, { ignoreQueryPrefix: true });
  return flagsFromObject(parsedQueryString);
};

export const FeatureFlagContext = React.createContext(
  _.merge({}, config.getDefaultFeatureFlags(), flagsFromEnvironment()),
);

export const FeatureFlagsProvider = ({ children }) => {
  const defaultFeatureFlags = React.useContext(FeatureFlagContext);
  const search = useLocation()?.search;
  const featureFlags = _.merge(
    {},
    defaultFeatureFlags,
    flagsFromQueryString(search),
  );
  const flagContextValue = _.isEqual(featureFlags, defaultFeatureFlags)
    ? defaultFeatureFlags
    : featureFlags;

  return (
    <FeatureFlagContext.Provider value={flagContextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};
